import React from "react";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import AccountIcon from "Assets/icon-article.svg"; //
import WoneIcon from "Assets/fill-9.svg";
import LegalIcon from "Assets/icon-article-new.svg";
import BiometricIcon from "Assets/healthMedicineHeartbeatHeart.svg";
import { LearnerSettings } from "apps/user/learner/api/types";
import useToggleableFeatureFlags from "apps/user/learner/hooks/use-toggleable-feature-flags";
import { useLocation, useNavigate } from "react-router-dom";
import { getDesktopBridge } from "desktop/bridge";

import constant from "Constant";
import styles from "./styles.module.scss";
import { useTranslation } from "lib/i18n/use-translation";

interface SettingsNavigationProps {
  settings: LearnerSettings;
}

interface NavigationButtonProps {
  title: string;
  route?: string;
  link?: string;
  email?: string;
}

interface NavigationButtonContainerProps {
  icon?: string;
  title?: string;
  children?: React.ReactNode;
}

const NavigationButton = ({
  title,
  route,
  link,
  email,
}: NavigationButtonProps) => {
  const location = useLocation();
  let { pathname } = location;
  const length = pathname.split("/").length;
  const routeName = pathname.split("/")[length - 1];
  const navigate = useNavigate();

  const handleNavigation = (route, link) => {
    if (route) {
      if (route === "settings") {
        navigate(`/user/profile/settings`);
      } else {
        navigate(`/user/profile/settings/${route}`);
      }
    } else if (link) {
      window.open(link, "_blank");
    } else if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <div
      onClick={() => handleNavigation(route, link)}
      className={
        routeName === route
          ? styles["navigation-button-active"]
          : styles["navigation-button"]
      }
    >
      <p>{title}</p>
      <ChevronIcon className={styles["right-chevron"]} alt="" />
    </div>
  );
};

const NavigationButtonContainer = ({
  icon,
  title,
  children,
}: NavigationButtonContainerProps) => {
  return (
    <div className={styles["main-container"]}>
      <div className={styles["header"]}>
        <img src={icon} alt={title} />
        {title && <p>{title}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
};

const SettingsNavigation = ({ settings }: SettingsNavigationProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["settings"]);
  const { flags: toggleableFlags } = useToggleableFeatureFlags();
  const hasDesktopSettings = Boolean(getDesktopBridge()?.settings);
  return (
    <div className={styles["settings-navigation"]}>
      <div onClick={() => navigate("/user/profile")} className={styles["back"]}>
        <ChevronIcon className={styles["back-chevron"]} alt="" />
        <p>{t("settings:navigation.backToProfile")}</p>
      </div>
      <NavigationButtonContainer
        icon={AccountIcon}
        title={t("settings:navigation.account")}
      >
        <NavigationButton title={settings.profile.title} route="settings" />
        <NavigationButton
          title={settings.notifications.title}
          route="notifications"
        />
        <NavigationButton
          title={settings.membership.title}
          route="membership"
        />
        <NavigationButton title={settings.calendar?.title} route="calendar" />
        {hasDesktopSettings && (
          <NavigationButton
            title={t("settings:desktop.sectionTitle")}
            route="desktop"
          />
        )}
        {toggleableFlags.length > 0 && (
          <NavigationButton
            title={t("settings:featureFlags.sectionTitle")}
            route="featureflags"
          />
        )}
      </NavigationButtonContainer>
      {settings.biometrics && (
        <NavigationButtonContainer
          icon={BiometricIcon}
          title={t("settings:navigation.biometrics")}
        >
          <NavigationButton
            title={settings.biometrics.providers.title}
            route="managedevices"
          />
          <NavigationButton
            title={settings.biometrics.howItWorks.title}
            route="how-it-works"
          />
        </NavigationButtonContainer>
      )}
      <NavigationButtonContainer icon={WoneIcon}>
        <NavigationButton
          title={settings.ourMission.title}
          route="ourmission"
        />
        <NavigationButton
          title={t("settings:home.item.contactUs")}
          email="support@walkingonearth.com"
        />
      </NavigationButtonContainer>
      <NavigationButtonContainer
        icon={LegalIcon}
        title={t("settings:navigation.legal")}
      >
        <NavigationButton
          title={t("settings:home.item.terms")}
          link={constant.TERMS_URL}
        />
        <NavigationButton
          title={t("settings:home.item.privacy")}
          link={constant.PRIVACY_POLICY_URL}
        />
      </NavigationButtonContainer>
    </div>
  );
};

export default SettingsNavigation;
